import { useState, useCallback, useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";

// ── Palette YOMA ──────────────────────────────────────────────────────────────
const GOLD    = "#C9A96E";
const INK     = "#1C1915";
const WARM    = "#F8F4EE";
const BORDER  = "#E7E1D8";
const MUTED   = "#8B7361";
const WHITE   = "#FFFFFF";
const SOFT    = "#F2EDE6";

const PIECES_DEFAULT = ["Salon", "Suite Parentale", "Chambre 1", "Chambre 2", "Salle de Bain", "Cuisine", "Entrée / Couloir", "Terrasse / Extérieur", "Bureau", "Autre"];
const CATEGORIES     = ["Mobilier", "Luminaire", "Textile & Literie", "Décoration", "Rangement", "Cuisine", "Salle de Bain", "Art", "Autre"];

const CURRENCIES = [
  { code: "EUR", symbol: "€", locale: "fr-FR" },
  { code: "USD", symbol: "$", locale: "en-US" },
  { code: "GBP", symbol: "£", locale: "en-GB" },
  { code: "CHF", symbol: "CHF", locale: "fr-CH" },
  { code: "MAD", symbol: "MAD", locale: "fr-MA" },
  { code: "AED", symbol: "AED", locale: "ar-AE" },
  { code: "XOF", symbol: "FCFA", locale: "fr-SN" },
];

function fmtMoney(n, currencyCode = "EUR") {
  const cur = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];
  const val = Number(n || 0);
  const rounded = Math.round(val).toLocaleString(cur.locale, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `${rounded} ${cur.symbol}`;
}
function fmtDec(n) {
  return Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// kept for PDF compat
function fmtEur(n) { return fmtDec(n) + " €"; }

// ── Proxy image CORS-safe (affichage + PDF) ───────────────────────────────────
// Toutes les images sont proxy-ées via weserv.nl pour éviter les blocages CORS
// (IKEA, Roche Bobois, Zara Home bloquent les requêtes cross-origin directes)
function proxyImg(rawUrl) {
  if (!rawUrl) return "";
  if (rawUrl.startsWith("data:") || rawUrl.includes("weserv.nl")) return rawUrl;
  return `https://images.weserv.nl/?url=${encodeURIComponent(rawUrl)}&w=600&output=jpg&il`;
}

// ── Extraction de prix depuis réponse microlink ───────────────────────────────
function extractPrice(d) {
  // Stratégie 1 : champ natif microlink (og:price:amount ou JSON-LD)
  const nativeAmt = Number(d.price?.amount ?? d["og:price:amount"] ?? 0);
  if (nativeAmt > 0) return nativeAmt;

  // Stratégie 2 : regex sur tous les champs texte disponibles
  const haystack = [d.description, d.title, d.url, JSON.stringify(d)].filter(Boolean).join(" ").slice(0, 3000);
  const patterns = [
    // MAD en premier (fréquent IKEA Maroc)
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:DH|Dhs?|MAD|درهم)/i,
    /(?:DH|Dhs?|MAD|درهم)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
    // Euros
    /(?:€|EUR)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:€|EUR)/i,
    // Dollars
    /(?:\$|USD)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:\$|USD)/i,
    // GBP
    /(?:£|GBP)\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:£|GBP)/i,
    // CHF
    /CHF\s*(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)/i,
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*CHF/i,
    // AED / XOF
    /(\d[\d\s]{1,8}(?:[.,]\d{1,2})?)\s*(?:AED|FCFA|XOF)/i,
    // "price": number dans JSON (OG / JSON-LD embedé)
    /"price"\s*:\s*"?(\d+(?:[.,]\d{1,2})?)"?/i,
    /content="(\d+(?:[.,]\d{1,2})?)" property="og:price:amount"/i,
  ];
  for (const pat of patterns) {
    const m = haystack.match(pat);
    if (m) {
      const num = parseFloat(m[1].replace(/\s/g, "").replace(",", "."));
      if (num > 0 && num < 999999) return num;
    }
  }
  return 0;
}

// ── Devise détectée depuis réponse microlink ──────────────────────────────────
function detectCurrency(d, rawUrl = "") {
  const raw = (d.price?.currency ?? d["og:price:currency"] ?? "").toUpperCase();
  if (CURRENCIES.find(c => c.code === raw)) return raw;
  // Détecter depuis l'URL (domaine pays)
  try {
    const host = new URL(rawUrl).hostname;
    if (host.includes(".ma") || host.includes("/ma/")) return "MAD";
    if (host.includes(".ae"))  return "AED";
    if (host.includes(".ch"))  return "CHF";
    if (host.includes(".co.uk")) return "GBP";
    if (host.includes(".com") && !host.includes(".fr") && !host.includes(".be")) return "USD";
  } catch {}
  const haystack = [d.description, d.title].filter(Boolean).join(" ");
  if (/DH|Dhs?|MAD|درهم/i.test(haystack)) return "MAD";
  if (/\$|USD/i.test(haystack)) return "USD";
  if (/£|GBP/i.test(haystack)) return "GBP";
  if (/CHF/i.test(haystack))   return "CHF";
  if (/AED/i.test(haystack))   return "AED";
  return "EUR";
}

// ── Référence produit depuis URL ──────────────────────────────────────────────
function extractRef(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    // IKEA : /p/nom-du-produit-s89542669/ → numéro après le dernier -s
    const ikeaS = u.pathname.match(/-s(\d{5,})\/?$/i);
    if (ikeaS) return ikeaS[1];
    // IKEA query string
    const artIkea = u.searchParams.get("ArticleNumber") || u.searchParams.get("articleNumber");
    if (artIkea) return artIkea;
    // IKEA /P-XXXXXXXX/ (ancien format)
    const ikeaP = u.pathname.match(/\/P-(\d{5,})/i);
    if (ikeaP) return ikeaP[1];
    // Zara Home : /p/XXXXXX
    const zara = u.pathname.match(/\/p\/([A-Z0-9-]+)/i);
    if (zara) return zara[1].toUpperCase();
    // Maisons du Monde : numéro final
    const mdm = u.pathname.match(/(\d{7,})\/?$/);
    if (mdm) return mdm[1];
    // Dernier segment slug → dernier token
    const segs = u.pathname.split("/").filter(Boolean);
    const last = segs[segs.length - 1] || "";
    const token = last.split("-").pop() || last;
    if (token.length > 3 && token.length < 22) return token.toUpperCase();
  } catch {}
  return "";
}

// ── Taux de change : toutes les devises relative à EUR (base) ─────────────────
// rates = { MAD: 10.85, USD: 1.08, GBP: 0.85, CHF: 0.96, ... }
// EUR est toujours la base (= 1)
function getRate(fromCode, toCode, rates) {
  if (!fromCode || !toCode || fromCode === toCode) return 1;
  if (!rates || Object.keys(rates).length === 0) return 1;
  const rateFrom = fromCode === "EUR" ? 1 : (rates[fromCode] ?? null);
  const rateTo   = toCode   === "EUR" ? 1 : (rates[toCode]   ?? null);
  if (!rateFrom || !rateTo) return 1;
  return rateTo / rateFrom;   // ex: EUR→MAD = 10.85 / 1 = 10.85
}

// ── Calcul complet avec conversion + frais d'import ──────────────────────────
// projectCurrency : devise d'affichage du projet (ex: "MAD")
// rates           : taux relatifs à EUR (depuis Frankfurter API)
// importMarkup    : % de frais d'import à appliquer si produit importé (ex: 20)
function calcItem(it, coeff, projectCurrency = "EUR", rates = {}, importMarkup = 0) {
  const pub      = Number(it.prixPublic || 0);
  const remise   = Number(it.remisePro || 0);
  const deviseProduit = it.deviseProduit || projectCurrency;

  // Prix d'achat dans la devise du produit (après remise pro)
  const achatDevise = pub * (1 - remise / 100);

  // Conversion vers la devise du projet
  const taux = getRate(deviseProduit, projectCurrency, rates);
  const achatConverti = achatDevise * taux;

  // Frais d'import si le produit est dans une devise différente
  const isImported = deviseProduit !== projectCurrency;
  const tauxImport = isImported ? (1 + importMarkup / 100) : 1;
  const achatImporte = achatConverti * tauxImport;

  // Marge agence appliquée APRÈS conversion + import
  const client   = achatImporte * coeff;
  const marge    = client - achatImporte;
  const qty      = Number(it.quantite || 1);

  return {
    pub, remise, deviseProduit, taux, isImported,
    achatDevise,      // prix achat dans devise origine
    achatConverti,    // converti dans devise projet (sans import)
    achatImporte,     // après frais d'import
    client,           // prix client final
    marge,
    qty,
    totalClient: client * qty,
    totalMarge:  marge  * qty,
  };
}

// ── Nouveau item vide ─────────────────────────────────────────────────────────
function newItem(overrides = {}) {
  return {
    id: Date.now() + Math.random(),
    photo: "", article: "Nouvel article", reference: "",
    source: "", url: "", categorie: "Mobilier",
    deviseProduit: "",   // devise dans laquelle est affiché le prix public
    prixPublic: 0, remisePro: 0, quantite: 1, note: "",
    ...overrides
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function FFEPage() {
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName]   = useState("");
  const [coeff, setCoeff]             = useState(1.20);
  const [currency, setCurrency]       = useState("MAD");  // devise du projet (affichage)
  const [importMarkup, setImportMarkup] = useState(25);   // % frais import (douane + transport)
  const [showMarges, setShowMarges]   = useState(false);  // privé vs client

  // ── Taux de change en temps réel (Frankfurter — gratuit, open source) ─────
  const [rates, setRates]             = useState({});     // { MAD: 10.85, USD: 1.08, ... }
  const [ratesAt, setRatesAt]         = useState(null);   // date de dernière màj
  const [ratesLoading, setRatesLoading] = useState(false);

  const fetchRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      // Frankfurter : taux relatifs à EUR pour toutes les devises CURRENCIES
      const codes = CURRENCIES.filter(c => c.code !== "EUR").map(c => c.code).join(",");
      const res  = await fetch(`https://api.frankfurter.app/latest?from=EUR&to=${codes}`, { signal: AbortSignal.timeout(8000) });
      const json = await res.json();
      if (json.rates) {
        setRates({ ...json.rates, EUR: 1 });
        setRatesAt(new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }));
      }
    } catch {
      // Silencieux — on garde les taux précédents ou on affiche "indisponible"
    } finally {
      setRatesLoading(false);
    }
  }, []);

  // Charger les taux au montage
  useEffect(() => { fetchRates(); }, [fetchRates]);

  // pièces : [{ id, name, items:[] }]
  const [pieces, setPieces] = useState(() =>
    PIECES_DEFAULT.slice(0, 4).map((name, i) => ({ id: i + 1, name, items: [] }))
  );
  const [activePiece, setActivePiece] = useState(1);
  const [view, setView]               = useState("cards"); // "cards" | "table"
  const [urlInput, setUrlInput]       = useState("");
  const [loadingUrl, setLoadingUrl]   = useState(false);
  const [urlError, setUrlError]       = useState("");
  const [newPieceName, setNewPieceName] = useState("");
  const [addingPiece, setAddingPiece]   = useState(false);

  // ── Helpers pièces ───────────────────────────────────────────────────────
  const currentPiece = pieces.find(p => p.id === activePiece) || pieces[0];

  function updateItems(pieceId, fn) {
    setPieces(prev => prev.map(p => p.id === pieceId ? { ...p, items: fn(p.items) } : p));
  }

  function addPiece() {
    const name = newPieceName.trim() || `Pièce ${pieces.length + 1}`;
    const id = Date.now();
    setPieces(prev => [...prev, { id, name, items: [] }]);
    setActivePiece(id);
    setNewPieceName("");
    setAddingPiece(false);
  }

  function removePiece(id) {
    if (pieces.length <= 1) return;
    if (!window.confirm("Supprimer cette pièce et tous ses articles ?")) return;
    const remaining = pieces.filter(p => p.id !== id);
    setPieces(remaining);
    if (activePiece === id) setActivePiece(remaining[0].id);
  }

  function renamePiece(id, name) {
    setPieces(prev => prev.map(p => p.id === id ? { ...p, name } : p));
  }

  // ── Scraper ──────────────────────────────────────────────────────────────
  const scrapeUrl = useCallback(async () => {
    const rawUrl = urlInput.trim();
    if (!rawUrl) return;
    setLoadingUrl(true);
    setUrlError("");
    try {
      // Tentative 1 : microlink sans prerender (rapide, ~1s)
      let d = {};
      let gotData = false;
      try {
        const r1 = await fetch(
          `https://api.microlink.io?url=${encodeURIComponent(rawUrl)}&screenshot=false&meta=true`,
          { signal: AbortSignal.timeout(8000) }
        );
        const j1 = await r1.json();
        if (j1.status === "success") { d = j1.data || {}; gotData = true; }
      } catch {}

      // Tentative 2 : prerender=true si peu de données récupérées (sites JS-lourds : IKEA, Roche Bobois…)
      const needsPrerender = !gotData || (!d.image?.url && !d.price?.amount);
      if (needsPrerender) {
        try {
          const r2 = await fetch(
            `https://api.microlink.io?url=${encodeURIComponent(rawUrl)}&screenshot=false&meta=true&prerender=true`,
            { signal: AbortSignal.timeout(18000) }
          );
          const j2 = await r2.json();
          if (j2.status === "success" && j2.data) d = j2.data;
        } catch {}
      }

      // Prix : extraction multi-stratégies
      const prix = extractPrice(d);

      // Devise du PRODUIT (pas celle du projet) — la devise dans laquelle est affiché le prix
      const deviseProduit = detectCurrency(d, rawUrl);

      // Image : proxy via weserv.nl pour éviter le CORS (IKEA, Zara Home, etc.)
      const rawPhoto = d.image?.url || d.logo?.url || "";
      const photo = proxyImg(rawPhoto);

      // Source / enseigne
      let source = d.publisher || "";
      if (!source) {
        try { source = new URL(rawUrl).hostname.replace("www.", "").split(".")[0].toUpperCase(); } catch {}
      }

      // Référence produit extraite depuis URL
      const reference = extractRef(rawUrl);

      // Nom produit : nettoyer les titres IKEA (souvent "Produit - IKEA France")
      const rawTitle = d.title || "Article";
      const article = rawTitle.replace(/\s*[-|–]\s*(IKEA|Zara Home|Roche Bobois|Maisons du Monde).*$/i, "").slice(0, 80).trim();

      updateItems(activePiece, items => [
        ...items,
        newItem({ photo, article, reference, source, url: rawUrl, prixPublic: prix, deviseProduit }),
      ]);
      setUrlInput("");

      if (prix === 0) {
        setUrlError("⚠ Prix non trouvé automatiquement — saisissez-le dans la carte (certains sites le masquent).");
      } else {
        setUrlError("");
      }
    } catch {
      setUrlError("Impossible de récupérer le produit. Vérifiez le lien ou ajoutez-le manuellement.");
    } finally {
      setLoadingUrl(false);
    }
  }, [urlInput, activePiece]);

  // ── Modif item ───────────────────────────────────────────────────────────
  function updItem(pieceId, itemId, field, value) {
    updateItems(pieceId, items => items.map(it => it.id === itemId ? { ...it, [field]: value } : it));
  }
  function delItem(pieceId, itemId) {
    updateItems(pieceId, items => items.filter(it => it.id !== itemId));
  }
  function dupItem(pieceId, itemId) {
    updateItems(pieceId, items => {
      const idx = items.findIndex(it => it.id === itemId);
      const copy = { ...items[idx], id: Date.now() + Math.random() };
      return [...items.slice(0, idx + 1), copy, ...items.slice(idx + 1)];
    });
  }

  // ── Totaux globaux ───────────────────────────────────────────────────────
  const totals = pieces.reduce((acc, p) => {
    p.items.forEach(it => {
      const c = calcItem(it, coeff, currency, rates, importMarkup);
      acc.public  += c.pub  * c.qty;
      acc.client  += c.totalClient;
      acc.marge   += c.totalMarge;
      acc.items   += 1;
    });
    return acc;
  }, { public: 0, client: 0, marge: 0, items: 0 });

  function pieceTotal(p) {
    return p.items.reduce((s, it) => s + calcItem(it, coeff, currency, rates, importMarkup).totalClient, 0);
  }

  // ── Export PDF ───────────────────────────────────────────────────────────
  function exportPDF() {
    const doc  = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W    = doc.internal.pageSize.getWidth();
    const H    = doc.internal.pageSize.getHeight();
    const today = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });

    // ── PAGE DE GARDE ──
    doc.setFillColor(28, 25, 21);
    doc.rect(0, 0, W, H, "F");

    // Trait doré
    doc.setDrawColor(201, 169, 110);
    doc.setLineWidth(0.5);
    doc.line(14, 50, W - 14, 50);
    doc.line(14, H - 50, W - 14, H - 50);

    doc.setTextColor(201, 169, 110);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("YOMA DESIGN · ARCHITECTURE INTÉRIEURE & COORDINATION", W / 2, 44, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    doc.setTextColor(255, 255, 255);
    doc.text("BOOK DÉCO", W / 2, 100, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(201, 169, 110);
    doc.text("Sélection FF&E — Furniture, Fixtures & Equipment", W / 2, 112, { align: "center" });

    if (projectName) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(projectName.toUpperCase(), W / 2, 140, { align: "center" });
    }
    if (clientName) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 140, 130);
      doc.text(`Client · ${clientName}`, W / 2, 150, { align: "center" });
    }

    doc.setFontSize(9);
    doc.setTextColor(100, 90, 80);
    doc.text(today, W / 2, H - 44, { align: "center" });

    // Budget récap garde
    doc.setFillColor(40, 36, 32);
    doc.roundedRect(30, H - 88, W - 60, 36, 4, 4, "F");
    doc.setTextColor(201, 169, 110);
    doc.setFontSize(9);
    doc.text("BUDGET TOTAL AMEUBLEMENT", W / 2, H - 80, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text(`${fmtDec(totals.client)} €`, W / 2, H - 66, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(130, 120, 110);
    doc.text(`${totals.items} article${totals.items > 1 ? "s" : ""}  ·  Coeff. ${coeff}`, W / 2, H - 59, { align: "center" });

    // ── UNE PAGE PAR PIÈCE ──
    pieces.filter(p => p.items.length > 0).forEach(p => {
      doc.addPage();

      // En-tête pièce
      doc.setFillColor(248, 244, 238);
      doc.rect(0, 0, W, 28, "F");
      doc.setDrawColor(201, 169, 110);
      doc.setLineWidth(0.8);
      doc.line(0, 28, W, 28);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.setTextColor(28, 25, 21);
      doc.text(p.name.toUpperCase(), 14, 17);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(139, 115, 97);
      doc.text(`${p.items.length} article${p.items.length > 1 ? "s" : ""}  ·  Budget : ${fmtDec(pieceTotal(p))} €`, W - 14, 17, { align: "right" });

      // Tableau articles
      const rows = p.items.map(it => {
        const c = calcItem(it, coeff, currency, rates, importMarkup);
        const row = [
          it.article.slice(0, 45),
          it.source,
          it.categorie,
          `${fmtDec(c.pub)} €`,
          String(c.qty),
          `${fmtDec(c.client)} €`,
          `${fmtDec(c.totalClient)} €`,
        ];
        if (showMarges) row.push(`${fmtDec(c.totalMarge)} €`);
        if (it.note)    row.push(it.note.slice(0, 30));
        return row;
      });

      const head = ["Article", "Source", "Catégorie", "Prix public", "Qté", "Prix client", "Total"];
      if (showMarges) head.push("Marge");

      doc.autoTable({
        startY: 34,
        head: [head],
        body: rows,
        theme: "grid",
        headStyles: { fillColor: [28, 25, 21], textColor: [201, 169, 110], fontStyle: "bold", fontSize: 8 },
        bodyStyles: { fontSize: 7.5, textColor: [40, 35, 30] },
        alternateRowStyles: { fillColor: [252, 249, 245] },
        columnStyles: {
          0: { cellWidth: 52 },
          3: { halign: "right" },
          4: { halign: "center" },
          5: { halign: "right" },
          6: { halign: "right", fontStyle: "bold" },
        },
        margin: { left: 14, right: 14 },
      });

      // Sous-total pièce
      const lastY = doc.lastAutoTable.finalY + 4;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(28, 25, 21);
      doc.text(`Sous-total ${p.name} : ${fmtDec(pieceTotal(p))} €`, W - 14, lastY + 5, { align: "right" });

      // Pied de page
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(180, 170, 160);
      doc.text("YOMA DESIGN · Book Déco FF&E · Document confidentiel", 14, H - 6);
      doc.text(today, W - 14, H - 6, { align: "right" });
    });

    // ── PAGE RÉCAP BUDGET ──
    doc.addPage();
    doc.setFillColor(28, 25, 21);
    doc.rect(0, 0, W, 30, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(201, 169, 110);
    doc.text("RÉCAPITULATIF BUDGET", 14, 18);

    const recapRows = pieces.filter(p => p.items.length > 0).map(p => {
      const tot = pieceTotal(p);
      return [p.name, `${p.items.length} art.`, `${fmtDec(tot)} €`, `${fmtDec(tot * 1.20)} € TTC`];
    });
    recapRows.push(["TOTAL", `${totals.items} art.`, `${fmtDec(totals.client)} €`, `${fmtDec(totals.client * 1.20)} € TTC`]);

    doc.autoTable({
      startY: 36,
      head: [["Pièce", "Articles", "Total HT", "Total TTC"]],
      body: recapRows,
      theme: "grid",
      headStyles: { fillColor: [28, 25, 21], textColor: [201, 169, 110], fontStyle: "bold" },
      bodyStyles: { fontSize: 10 },
      alternateRowStyles: { fillColor: [252, 249, 245] },
      columnStyles: { 2: { halign: "right", fontStyle: "bold" }, 3: { halign: "right" } },
      didParseCell: (data) => {
        if (data.row.index === recapRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [248, 244, 238];
          data.cell.styles.textColor = [28, 25, 21];
        }
      },
      margin: { left: 14, right: 14 },
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(180, 170, 160);
    doc.text("YOMA DESIGN · Book Déco FF&E · Document confidentiel", 14, H - 6);
    doc.text(today, W - 14, H - 6, { align: "right" });

    doc.save(`YOMA_Book_Deco_${(projectName || "FFE").replace(/\s+/g, "_")}_${new Date().toLocaleDateString("fr-FR").replace(/\//g, "-")}.pdf`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: WARM, fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        .ffe-input { background:${WHITE}; border:1px solid ${BORDER}; border-radius:6px; padding:9px 12px; font-size:13px; color:${INK}; width:100%; box-sizing:border-box; font-family:'DM Sans',sans-serif; }
        .ffe-input:focus { outline:none; border-color:${GOLD}; }
        .ffe-btn { border:none; border-radius:7px; padding:10px 18px; cursor:pointer; font-size:13px; font-weight:600; transition:all .15s; font-family:'DM Sans',sans-serif; }
        .ffe-btn:hover:not(:disabled) { opacity:.85; transform:translateY(-1px); }
        .ffe-btn:disabled { opacity:.45; cursor:default; }
        .piece-tab { padding:9px 16px; border-radius:8px; cursor:pointer; font-size:13px; font-weight:500; transition:all .15s; border:1.5px solid transparent; white-space:nowrap; }
        .piece-tab:hover { border-color:${BORDER}; }
        .card-item { background:${WHITE}; border-radius:14px; border:1px solid ${BORDER}; overflow:hidden; transition:all .2s; }
        .card-item:hover { border-color:${GOLD}; box-shadow:0 6px 24px rgba(0,0,0,.08); transform:translateY(-2px); }
        .edit-overlay { display:none; }
        .card-item:hover .edit-overlay { display:flex; }
        .tr-item:hover { background:${SOFT} !important; }
        .cell-inp { border:none; background:transparent; font-size:13px; color:${INK}; width:100%; padding:3px; font-family:'DM Sans',sans-serif; }
        .cell-inp:focus { outline:1px solid ${GOLD}; border-radius:3px; background:${WHITE}; }
        .cell-inp::-webkit-inner-spin-button,.cell-inp::-webkit-outer-spin-button { opacity:0; }
      `}</style>

      {/* ── SIDEBAR PIÈCES ── */}
      <aside style={{ width: 220, background: INK, display: "flex", flexDirection: "column", padding: "24px 12px", flexShrink: 0 }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28, paddingBottom: 20, borderBottom: "1px solid rgba(255,255,255,.1)" }}>
          <div style={{ color: GOLD, fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, letterSpacing: 3 }}>YOMA</div>
          <div style={{ color: "#8A7B6D", fontSize: 9, letterSpacing: "0.2em", marginTop: 3 }}>BOOK DÉCO FF&E</div>
        </div>

        {/* Infos projet */}
        <div style={{ marginBottom: 20 }}>
          <input className="ffe-input" placeholder="Nom du projet…" value={projectName} onChange={e => setProjectName(e.target.value)}
            style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", color: WHITE, marginBottom: 8 }} />
          <input className="ffe-input" placeholder="Nom du client…" value={clientName} onChange={e => setClientName(e.target.value)}
            style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", color: WHITE }} />
        </div>

        {/* Devise du projet */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ color: "#8A7B6D", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 6 }}>DEVISE DU PROJET</div>
          <select value={currency} onChange={e => setCurrency(e.target.value)}
            style={{ width: "100%", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.18)", color: WHITE, borderRadius: 6, padding: "7px 10px", fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>
            {CURRENCIES.map(c => <option key={c.code} value={c.code} style={{ background: INK }}>{c.code} — {c.symbol}</option>)}
          </select>
        </div>

        {/* Taux de change en temps réel */}
        <div style={{ marginBottom: 16, padding: "10px 12px", background: "rgba(255,255,255,.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ color: "#8A7B6D", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>TAUX DU JOUR</div>
            <button onClick={fetchRates} disabled={ratesLoading}
              style={{ background: "none", border: "none", color: GOLD, fontSize: 12, cursor: "pointer", padding: 0 }} title="Actualiser">
              {ratesLoading ? "⏳" : "↻"}
            </button>
          </div>
          {Object.keys(rates).length === 0 ? (
            <div style={{ color: "#5A4E46", fontSize: 11 }}>{ratesLoading ? "Chargement…" : "Indisponible"}</div>
          ) : (
            <>
              {/* Afficher les taux vers la devise du projet */}
              {CURRENCIES.filter(c => c.code !== currency && rates[c.code] !== undefined).slice(0, 4).map(c => {
                const r = getRate(c.code, currency, rates);
                return (
                  <div key={c.code} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ color: "#6A5D52", fontSize: 11 }}>1 {c.symbol}</span>
                    <span style={{ color: WHITE, fontSize: 11, fontWeight: 600 }}>= {r.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {CURRENCIES.find(x=>x.code===currency)?.symbol || currency}</span>
                  </div>
                );
              })}
              {ratesAt && <div style={{ color: "#4A3E35", fontSize: 9, marginTop: 4 }}>Mis à jour {ratesAt}</div>}
            </>
          )}
        </div>

        {/* Coeff marge agence */}
        <div style={{ marginBottom: 12, padding: "12px 14px", background: "rgba(201,169,110,.12)", borderRadius: 8, border: "1px solid rgba(201,169,110,.25)" }}>
          <div style={{ color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>MARGE AGENCE</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" step="0.05" min="1" value={coeff} onChange={e => setCoeff(parseFloat(e.target.value) || 1)}
              className="ffe-input" style={{ background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.15)", color: WHITE, textAlign: "center", width: 70, padding: "6px 8px" }} />
            <span style={{ color: GOLD, fontWeight: 700, fontSize: 15 }}>×{coeff}</span>
          </div>
          <div style={{ color: "#8A7B6D", fontSize: 11, marginTop: 5 }}>Marge : {Math.round((coeff - 1) * 100)}%</div>
        </div>

        {/* Frais d'import (si produit importé = devise différente) */}
        <div style={{ marginBottom: 20, padding: "12px 14px", background: "rgba(255,120,50,.08)", borderRadius: 8, border: "1px solid rgba(255,120,50,.2)" }}>
          <div style={{ color: "#E8905A", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4 }}>FRAIS D'IMPORT</div>
          <div style={{ color: "#8A7B6D", fontSize: 10, marginBottom: 8 }}>Douane + transport (auto si devise ≠ projet)</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="number" step="1" min="0" max="100" value={importMarkup} onChange={e => setImportMarkup(Number(e.target.value) || 0)}
              className="ffe-input" style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,120,50,.3)", color: WHITE, textAlign: "center", width: 60, padding: "6px 8px" }} />
            <span style={{ color: "#E8905A", fontWeight: 700, fontSize: 14 }}>%</span>
          </div>
          <div style={{ color: "#5A4E46", fontSize: 10, marginTop: 5 }}>Maroc : 20–35% · UE vers UE : 0%</div>
        </div>

        {/* Pièces */}
        <div style={{ flex: 1, overflow: "auto" }}>
          <div style={{ color: "#8A7B6D", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10 }}>PIÈCES</div>
          {pieces.map(p => {
            const tot = pieceTotal(p);
            const active = p.id === activePiece;
            return (
              <div key={p.id} onClick={() => setActivePiece(p.id)} style={{
                padding: "10px 12px", borderRadius: 8, marginBottom: 4, cursor: "pointer",
                background: active ? "rgba(201,169,110,.18)" : "transparent",
                border: active ? "1px solid rgba(201,169,110,.4)" : "1px solid transparent",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ color: active ? GOLD : "#BBA990", fontSize: 13, fontWeight: active ? 600 : 400 }}>{p.name}</span>
                  <button onClick={e => { e.stopPropagation(); removePiece(p.id); }}
                    style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 12, padding: "0 2px", lineHeight: 1 }}>×</button>
                </div>
                {p.items.length > 0 && (
                  <div style={{ color: "#6A5D52", fontSize: 11, marginTop: 3 }}>{p.items.length} art. · {fmtMoney(tot, currency)}</div>
                )}
              </div>
            );
          })}

          {/* Ajouter pièce */}
          {addingPiece ? (
            <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
              <input autoFocus className="ffe-input" placeholder="Nom…" value={newPieceName} onChange={e => setNewPieceName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") addPiece(); if (e.key === "Escape") setAddingPiece(false); }}
                style={{ background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", color: WHITE, padding: "7px 10px", fontSize: 12 }} />
              <button onClick={addPiece} className="ffe-btn" style={{ background: GOLD, color: INK, padding: "7px 10px" }}>✓</button>
            </div>
          ) : (
            <button onClick={() => setAddingPiece(true)} className="ffe-btn"
              style={{ width: "100%", background: "rgba(255,255,255,.05)", color: "#8A7B6D", marginTop: 6, border: "1px dashed rgba(255,255,255,.15)", padding: "8px" }}>
              + Ajouter une pièce
            </button>
          )}
        </div>

        {/* Totaux globaux */}
        <div style={{ marginTop: 20, padding: "14px", background: "rgba(255,255,255,.04)", borderRadius: 8, borderTop: "1px solid rgba(255,255,255,.08)" }}>
          <div style={{ color: "#8A7B6D", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 8 }}>BUDGET TOTAL</div>
          <div style={{ color: WHITE, fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display',serif" }}>{fmtMoney(totals.client, currency)}</div>
          <div style={{ color: "#5A4E46", fontSize: 11, marginTop: 3 }}>TTC ≈ {fmtMoney(totals.client * 1.20, currency)}</div>
          {showMarges && <div style={{ color: GOLD, fontSize: 11, marginTop: 4 }}>Marge agence : {fmtMoney(totals.marge, currency)}</div>}
        </div>

        {/* Actions */}
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={exportPDF} className="ffe-btn" style={{ background: GOLD, color: INK, textAlign: "center" }}>
            📄 Exporter PDF
          </button>
          <button onClick={() => setShowMarges(m => !m)} className="ffe-btn"
            style={{ background: "transparent", color: "#8A7B6D", border: "1px solid rgba(255,255,255,.1)", textAlign: "center", fontSize: 12 }}>
            {showMarges ? "🔒 Masquer marges" : "👁 Voir mes marges"}
          </button>
        </div>
      </aside>

      {/* ── CONTENU PRINCIPAL ── */}
      <main style={{ flex: 1, padding: 28, overflow: "auto" }}>
        {/* En-tête pièce */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <input
              value={currentPiece?.name || ""}
              onChange={e => renamePiece(activePiece, e.target.value)}
              style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, fontWeight: 700, color: INK, background: "transparent", border: "none", outline: "none", padding: 0, cursor: "text" }}
            />
            <div style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>
              {currentPiece?.items.length || 0} article{(currentPiece?.items.length || 0) > 1 ? "s" : ""}
              {currentPiece?.items.length > 0 && <span> · Sous-total : <strong style={{ color: INK }}>{fmtMoney(pieceTotal(currentPiece), currency)}</strong></span>}
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Switch vue */}
            <div style={{ display: "flex", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden" }}>
              {[["cards", "⊞ Visuels"], ["table", "≡ Tableau"]].map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} className="ffe-btn" style={{
                  borderRadius: 0, background: view === v ? INK : "transparent",
                  color: view === v ? WHITE : MUTED, padding: "8px 16px", fontWeight: view === v ? 600 : 400,
                }}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Barre d'ajout URL */}
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: 16, marginBottom: 24 }}>
          <div style={{ display: "flex", gap: 10, marginBottom: urlError ? 8 : 0 }}>
            <input
              className="ffe-input"
              placeholder="Collez un lien IKEA, Zara Home, Roche Bobois, Maisons du Monde…"
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && scrapeUrl()}
              style={{ flex: 1 }}
            />
            <button className="ffe-btn" onClick={scrapeUrl} disabled={loadingUrl || !urlInput.trim()}
              style={{ background: GOLD, color: INK, minWidth: 130 }}>
              {loadingUrl ? "⏳ Analyse en cours…" : "➕ Ajouter le produit"}
            </button>
            <button className="ffe-btn" onClick={() => updateItems(activePiece, items => [...items, newItem()])}
              style={{ background: SOFT, color: INK, border: `1px solid ${BORDER}` }}>
              + Manuel
            </button>
          </div>
          {urlError && <p style={{ margin: 0, color: "#B85C3A", fontSize: 12 }}>⚠ {urlError}</p>}
        </div>

        {/* ── VUE CARTES ── */}
        {view === "cards" && (
          <>
            {currentPiece?.items.length === 0 ? (
              <EmptyState onManual={() => updateItems(activePiece, items => [...items, newItem()])} />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 20 }}>
                {currentPiece.items.map(it => (
                  <CardItem key={it.id} it={it} coeff={coeff} currency={currency}
                    rates={rates} importMarkup={importMarkup} showMarges={showMarges}
                    onUpdate={(f, v) => updItem(activePiece, it.id, f, v)}
                    onDelete={() => delItem(activePiece, it.id)}
                    onDuplicate={() => dupItem(activePiece, it.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── VUE TABLEAU ── */}
        {view === "table" && (
          <>
            {currentPiece?.items.length === 0 ? (
              <EmptyState onManual={() => updateItems(activePiece, items => [...items, newItem()])} />
            ) : (
              <div style={{ background: WHITE, borderRadius: 12, border: `1px solid ${BORDER}`, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: INK }}>
                      {["Visuel","Article / Réf.","Devise","Catégorie","Prix public","Remise %","Prix achat (converti)","Prix client","Qté","Total","Note",""].map((h, i) => (
                        <th key={i} style={{ padding: "11px 10px", color: GOLD, fontWeight: 700, fontSize: 10, letterSpacing: "0.06em", textAlign: i >= 4 && i <= 9 ? "right" : "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentPiece.items.map((it, idx) => {
                      const c = calcItem(it, coeff, currency, rates, importMarkup);
                      return (
                        <tr key={it.id} className="tr-item" style={{ background: idx % 2 === 0 ? WHITE : "#FAF8F5", borderBottom: `1px solid ${BORDER}` }}>
                          <td style={{ padding: 8, width: 70 }}>
                            {it.photo
                              ? <img src={it.photo} alt="" style={{ width: 60, height: 48, objectFit: "cover", borderRadius: 6, border: `1px solid ${BORDER}` }} />
                              : <div style={{ width: 60, height: 48, background: SOFT, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🛋</div>
                            }
                          </td>
                          <td style={{ padding: 8, minWidth: 180 }}>
                            <input className="cell-inp" value={it.article} onChange={e => updItem(activePiece, it.id, "article", e.target.value)} style={{ fontWeight: 600 }} />
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
                              {it.url
                                ? <a href={it.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: MUTED }}>🔗 {it.source}</a>
                                : <input className="cell-inp" value={it.source} onChange={e => updItem(activePiece, it.id, "source", e.target.value)} style={{ fontSize: 11, color: MUTED }} placeholder="Source…" />
                              }
                              {(it.reference !== undefined) && (
                                <input className="cell-inp" value={it.reference || ""} onChange={e => updItem(activePiece, it.id, "reference", e.target.value)}
                                  style={{ fontSize: 10, color: "#B0A090", fontFamily: "monospace", maxWidth: 90 }} placeholder="Réf…" />
                              )}
                            </div>
                          </td>
                          {/* Devise produit */}
                          <td style={{ padding: 8 }}>
                            <select value={it.deviseProduit || currency} onChange={e => updItem(activePiece, it.id, "deviseProduit", e.target.value)}
                              style={{ border: `1px solid ${c.isImported ? "rgba(255,120,50,.4)" : BORDER}`, borderRadius: 4, padding: "3px 5px", fontSize: 11, color: c.isImported ? "#C06A30" : MUTED, background: c.isImported ? "rgba(255,120,50,.06)" : WHITE }}>
                              {CURRENCIES.map(cur => <option key={cur.code} value={cur.code}>{cur.code}</option>)}
                            </select>
                            {c.isImported && <div style={{ fontSize: 9, color: "#C06A30", marginTop: 2 }}>✈ ×{c.taux.toLocaleString("fr-FR",{maximumFractionDigits:2})} +{importMarkup}%</div>}
                          </td>
                          <td style={{ padding: 8 }}>
                            <select value={it.categorie} onChange={e => updItem(activePiece, it.id, "categorie", e.target.value)}
                              style={{ border: `1px solid ${BORDER}`, borderRadius: 4, padding: "4px 6px", fontSize: 12, color: INK, background: WHITE }}>
                              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: 8, textAlign: "right" }}>
                            <input className="cell-inp" type="number" min="0" step="10" value={it.prixPublic}
                              onChange={e => updItem(activePiece, it.id, "prixPublic", e.target.value)} style={{ textAlign: "right", width: 80 }} />
                            <span style={{ fontSize: 11, color: MUTED }}> {CURRENCIES.find(cur=>cur.code===(it.deviseProduit||currency))?.symbol||currency}</span>
                          </td>
                          <td style={{ padding: 8, textAlign: "right" }}>
                            <input className="cell-inp" type="number" min="0" max="100" step="1" value={it.remisePro}
                              onChange={e => updItem(activePiece, it.id, "remisePro", e.target.value)} style={{ textAlign: "right", width: 50 }} /> %
                          </td>
                          <td style={{ padding: 8, textAlign: "right", fontSize: 12 }}>
                            <div style={{ color: MUTED }}>{fmtMoney(c.achatConverti, currency)}</div>
                            {c.isImported && <div style={{ color: "#C06A30", fontSize: 10 }}>+imp: {fmtMoney(c.achatImporte, currency)}</div>}
                          </td>
                          <td style={{ padding: 8, textAlign: "right", color: INK, fontWeight: 600 }}>{fmtMoney(c.client, currency)}</td>
                          <td style={{ padding: 8, textAlign: "right" }}>
                            <input className="cell-inp" type="number" min="1" value={it.quantite}
                              onChange={e => updItem(activePiece, it.id, "quantite", e.target.value)} style={{ textAlign: "center", width: 40 }} />
                          </td>
                          <td style={{ padding: 8, textAlign: "right", fontWeight: 700, color: INK }}>{fmtMoney(c.totalClient, currency)}</td>
                          <td style={{ padding: 8 }}>
                            <input className="cell-inp" value={it.note} onChange={e => updItem(activePiece, it.id, "note", e.target.value)} placeholder="Note…" style={{ fontSize: 12, color: MUTED, minWidth: 100 }} />
                          </td>
                          <td style={{ padding: 8, textAlign: "center", whiteSpace: "nowrap" }}>
                            <button onClick={() => dupItem(activePiece, it.id)} title="Dupliquer" style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14 }}>📋</button>
                            <button onClick={() => delItem(activePiece, it.id)} title="Supprimer" style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14 }}>🗑</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Sous-total tableau */}
                <div style={{ background: SOFT, borderTop: `2px solid ${GOLD}`, padding: "12px 20px", display: "flex", justifyContent: "flex-end", gap: 32 }}>
                  {showMarges && <div style={{ textAlign: "right" }}><div style={{ fontSize: 11, color: MUTED }}>Marge agence</div><div style={{ fontSize: 16, fontWeight: 700, color: GOLD }}>{fmtMoney(currentPiece.items.reduce((s,it)=>s+calcItem(it,coeff,currency,rates,importMarkup).totalMarge,0), currency)}</div></div>}
                  <div style={{ textAlign: "right" }}><div style={{ fontSize: 11, color: MUTED }}>Sous-total {currentPiece.name}</div><div style={{ fontSize: 20, fontWeight: 700, color: INK }}>{fmtMoney(pieceTotal(currentPiece), currency)}</div></div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// ── Card Item ──────────────────────────────────────────────────────────────────
function CardItem({ it, coeff, currency = "EUR", rates = {}, importMarkup = 0, showMarges, onUpdate, onDelete, onDuplicate }) {
  const c = calcItem(it, coeff, currency, rates, importMarkup);
  const sym = CURRENCIES.find(cur => cur.code === currency)?.symbol || currency;
  const symProd = CURRENCIES.find(cur => cur.code === c.deviseProduit)?.symbol || c.deviseProduit || sym;
  return (
    <div className="card-item" style={{ position: "relative" }}>
      {/* Photo */}
      <div style={{ width: "100%", height: 180, background: SOFT, position: "relative", overflow: "hidden" }}>
        {it.photo
          ? <img src={it.photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 44, color: BORDER }}>🛋</div>
        }
        {/* Overlay actions */}
        <div className="edit-overlay" style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(28,25,21,.6)", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <button onClick={onDuplicate} style={{ background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.3)", color: WHITE, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}>📋 Dupliquer</button>
          <button onClick={onDelete} style={{ background: "rgba(184,92,58,.8)", border: "none", color: WHITE, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}>🗑 Supprimer</button>
        </div>
        {/* Badge source */}
        {it.source && <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(28,25,21,.75)", color: GOLD, fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 4, letterSpacing: "0.1em" }}>{it.source}</div>}
      </div>

      {/* Infos */}
      <div style={{ padding: "14px 16px 16px" }}>
        <input
          value={it.article}
          onChange={e => onUpdate("article", e.target.value)}
          style={{ width: "100%", border: "none", background: "transparent", fontSize: 14, fontWeight: 600, color: INK, padding: 0, marginBottom: 2, fontFamily: "'DM Sans',sans-serif" }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
          {it.reference !== undefined && (
            <input value={it.reference || ""} onChange={e => onUpdate("reference", e.target.value)} placeholder="Réf. produit…"
              style={{ border: "none", background: "transparent", fontSize: 10, color: "#B0A090", padding: 0, fontFamily: "monospace", flex: 1, minWidth: 0 }} />
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <select value={it.categorie} onChange={e => onUpdate("categorie", e.target.value)}
            style={{ width: "100%", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "4px 8px", fontSize: 11, color: MUTED, background: WARM, marginBottom: 8 }}>
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          {/* Quantité — stepper +/− */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, border: `1px solid ${BORDER}`, borderRadius: 6, overflow: "hidden", background: WHITE }}>
            <button onClick={() => onUpdate("quantite", Math.max(1, (Number(it.quantite) || 1) - 1))}
              style={{ width: 32, height: 32, border: "none", background: SOFT, color: INK, fontSize: 16, cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>−</button>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "2px 0" }}>
              <span style={{ fontSize: 9, color: MUTED, fontWeight: 700, letterSpacing: "0.1em", lineHeight: 1 }}>QTÉ</span>
              <input type="number" min="1" value={it.quantite} onChange={e => onUpdate("quantite", Math.max(1, Number(e.target.value) || 1))}
                style={{ width: "100%", border: "none", background: "transparent", fontSize: 14, textAlign: "center", color: INK, fontWeight: 700, padding: 0, outline: "none" }} />
            </div>
            <button onClick={() => onUpdate("quantite", (Number(it.quantite) || 1) + 1)}
              style={{ width: 32, height: 32, border: "none", background: SOFT, color: INK, fontSize: 16, cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>+</button>
          </div>
        </div>

        {/* Devise produit + badge importé */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8 }}>
          <select value={it.deviseProduit || currency} onChange={e => onUpdate("deviseProduit", e.target.value)}
            style={{ border: `1px solid ${BORDER}`, borderRadius: 4, padding: "3px 7px", fontSize: 11, color: MUTED, background: WARM }}>
            {CURRENCIES.map(cur => <option key={cur.code} value={cur.code}>{cur.code}</option>)}
          </select>
          {c.isImported && (
            <span style={{ background: "rgba(255,120,50,.12)", color: "#C06A30", border: "1px solid rgba(255,120,50,.3)", borderRadius: 4, fontSize: 10, padding: "2px 7px", fontWeight: 700 }}>
              ✈ IMPORTÉ
            </span>
          )}
        </div>

        {/* Prix */}
        <div style={{ background: SOFT, borderRadius: 8, padding: "10px 12px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: MUTED }}>Prix public</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input type="number" min="0" step="10" value={it.prixPublic} onChange={e => onUpdate("prixPublic", e.target.value)}
                style={{ width: 70, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 6px", fontSize: 12, textAlign: "right", color: MUTED, background: WHITE }} />
              <span style={{ fontSize: 12, color: MUTED }}>{symProd}</span>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: c.isImported ? 4 : 6 }}>
            <span style={{ fontSize: 11, color: MUTED }}>Remise pro</span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input type="number" min="0" max="100" step="1" value={it.remisePro} onChange={e => onUpdate("remisePro", e.target.value)}
                style={{ width: 50, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 6px", fontSize: 12, textAlign: "right", color: MUTED, background: WHITE }} />
              <span style={{ fontSize: 12, color: MUTED }}>%</span>
            </div>
          </div>

          {/* Détail conversion si produit importé */}
          {c.isImported && (
            <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 6, marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                <span style={{ fontSize: 10, color: "#8A7B6D" }}>
                  Taux {c.deviseProduit}→{currency} ×{c.taux.toLocaleString("fr-FR",{minimumFractionDigits:2,maximumFractionDigits:2})}
                </span>
                <span style={{ fontSize: 10, color: "#8A7B6D" }}>{fmtMoney(c.achatConverti, currency)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 10, color: "#C06A30" }}>+ Frais import {importMarkup}%</span>
                <span style={{ fontSize: 10, color: "#C06A30", fontWeight: 600 }}>{fmtMoney(c.achatImporte, currency)}</span>
              </div>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>Prix client × {it.quantite}</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: INK }}>{fmtMoney(c.totalClient, currency)}</span>
          </div>
          {showMarges && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ fontSize: 11, color: MUTED }}>Marge agence</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: GOLD }}>{fmtMoney(c.totalMarge, currency)}</span>
            </div>
          )}
        </div>

        {/* Lien */}
        {it.url && <a href={it.url} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: 8, fontSize: 11, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🔗 {it.url.slice(0, 45)}…</a>}
      </div>
    </div>
  );
}

// ── État vide ──────────────────────────────────────────────────────────────────
function EmptyState({ onManual }) {
  return (
    <div style={{ textAlign: "center", padding: "70px 20px", color: MUTED }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>🛋️</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: INK, marginBottom: 8 }}>Aucun article dans cette pièce</div>
      <p style={{ fontSize: 14, maxWidth: 380, margin: "0 auto 20px" }}>
        Collez un lien produit ci-dessus ou ajoutez un article manuellement.
      </p>
      <button onClick={onManual} style={{ background: GOLD, color: INK, border: "none", borderRadius: 8, padding: "12px 24px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
        + Ajouter un article
      </button>
    </div>
  );
}
